import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { combineEventHandlers } from '@weave-design/utils';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

const _excluded = ["onClick", "item"],
      _excluded2 = ["children", "inputValue"];
/**
 * @typedef {import("downshift").DownshiftProps<any>} Props
 * @property {any[]} selectedItems
 */

/**
 * @typedef {Object} GetRemoveButtonPropsOptions
 * @property {function(MouseEvent)} onClick
 * @property {any} item
 */

/**
 * @typedef {import("downshift").ControllerStateAndHelpers<any>} ControllerStateAndHelpers
 * @property {function(GetRemoveButtonPropsOptions): any} getRemoveButtonProps
 * @property {any[]} selectedItems
 */

class MultiDownshift extends React.Component {
  constructor() {
    var _this;

    super(...arguments);
    _this = this;

    _defineProperty(this, "state", {
      selectedItems: this.getDefaultSelectedItem(),
      lastClickedItem: ""
    });

    _defineProperty(this, "getRemoveButtonProps", function () {
      let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      let {
        onClick,
        item
      } = _ref,
          props = _objectWithoutProperties(_ref, _excluded);

      /**
       * @param {MouseEvent} event
       */
      const handleRemoveButtonClick = event => {
        event.stopPropagation();

        _this.unselectItem(item);
      };

      const handleClick = combineEventHandlers(onClick, handleRemoveButtonClick);
      return _objectSpread2(_objectSpread2({}, props), {}, {
        onClick: handleClick
      });
    });

    _defineProperty(this, "stateReducer", (state, changes) => {
      switch (changes.type) {
        case Downshift.stateChangeTypes.clickItem:
          return _objectSpread2(_objectSpread2({}, changes), {}, {
            isOpen: true
          });

        default:
          return changes;
      }
    });

    _defineProperty(this, "handleSelect", (selectedItem, downshift) => {
      const selectedItems = this.getSelectedItems();
      const triggerChange = this.createChangeTrigger(downshift, selectedItem);

      if (selectedItems.includes(selectedItem)) {
        this.unselectItem(selectedItem, triggerChange);
      } else {
        this.selectItem(selectedItem, triggerChange);
      }
    });

    _defineProperty(this, "renderPresenter", downshift => this.props.children(this.getStateAndHelpers(downshift)));
  }

  getDefaultSelectedItem() {
    const {
      initialSelectedItems
    } = this.props;
    return initialSelectedItems;
  }
  /**
   * @returns {any[]}
   */


  getSelectedItems() {
    const controlledSelectedItems = this.props.selectedItems;
    const {
      selectedItems
    } = this.state;
    return controlledSelectedItems !== undefined ? controlledSelectedItems : selectedItems;
  }
  /**
   * @param {import("downshift").ControllerStateAndHelpers<any>} downshift
   * @returns {ControllerStateAndHelpers}
   */


  getStateAndHelpers(downshift) {
    const selectedItems = this.getSelectedItems();
    const {
      getRemoveButtonProps
    } = this;
    return _objectSpread2(_objectSpread2({}, downshift), {}, {
      getRemoveButtonProps,
      selectedItems
    });
  }
  /**
   * @param {GetRemoveButtonPropsOptions} options
   * @returns {any}
   */


  /**
   * @returns {import("downshift").ControllerStateAndHelpers}
   */
  getDownshiftProps() {
    const _this$props = this.props,
          {
      children,
      inputValue
    } = _this$props,
          otherProps = _objectWithoutProperties(_this$props, _excluded2);

    const controlledInputValue = inputValue !== undefined ? inputValue : this.selectedItemsToString();
    return _objectSpread2(_objectSpread2({}, otherProps), {}, {
      inputValue: controlledInputValue,
      onSelect: this.handleSelect,
      stateReducer: this.stateReducer
    });
  }
  /**
   * @param {import("downshift").DownshiftState<any>} state
   * @param {import("downshift").StateChangeOptions<any>} changes
   * @returns {import("downshift").Partial<StateChangeOptions<any>>}
   */


  /**
   * @param {import("downshift").ControllerStateAndHelpers} downshift
   */
  createChangeTrigger(downshift, selectedItem) {
    const multiDownshift = this.getStateAndHelpers(downshift);
    return () => {
      const {
        onChange
      } = this.props;
      const selectedItems = this.getSelectedItems();

      if (this.state.lastClickedItem !== selectedItem) {
        this.setState({
          lastClickedItem: selectedItem
        });
      }

      if (onChange) {
        onChange(selectedItems, multiDownshift);

        if (this.state.lastClickedItem === selectedItem) {
          onChange(selectedItem, multiDownshift);
        }
      }
    };
  }
  /**
   * @returns {string}
   * @todo Add intelligent formatting
   */


  selectedItemsToString() {
    const {
      itemToString
    } = this.props;
    const selectedItems = this.getSelectedItems();
    return selectedItems.map(itemToString).join(", ");
  }
  /**
   * @param {any} item
   * @param {Function} done
   */


  unselectItem(item, done) {
    const selectedItems = this.getSelectedItems();
    const nextSelectedItems = selectedItems.filter(selectedItem => selectedItem !== item);
    this.setState({
      selectedItems: nextSelectedItems
    }, done);
  }
  /**
   * @param {any} removedItem
   * @param {Function} done
   */


  selectItem(item, done) {
    const selectedItems = this.getSelectedItems();
    const nextSelectedItems = [...selectedItems, item];
    this.setState({
      selectedItems: nextSelectedItems
    }, done);
  }
  /**
   * @param {import("downshift").ControllerStateAndHelpers} downshift
   */


  render() {
    return /*#__PURE__*/React.createElement(Downshift, this.getDownshiftProps(), this.renderPresenter);
  }

}

_defineProperty(MultiDownshift, "propTypes", _objectSpread2(_objectSpread2({}, Downshift.propTypes), {}, {
  /** Controlled: the currently selected items */
  selectedItems: PropTypes.arrayOf(PropTypes.any),

  /** Items selected by default */
  initialSelectedItems: PropTypes.arrayOf(PropTypes.any)
}));

_defineProperty(MultiDownshift, "defaultProps", {
  initialSelectedItems: []
});

MultiDownshift.__docgenInfo = {
  "description": "@typedef {import(\"downshift\").ControllerStateAndHelpers<any>} ControllerStateAndHelpers\n@property {function(GetRemoveButtonPropsOptions): any} getRemoveButtonProps\n@property {any[]} selectedItems",
  "displayName": "MultiDownshift",
  "props": {
    "selectedItems": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Controlled: the currently selected items"
    },
    "initialSelectedItems": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Items selected by default",
      "defaultValue": {
        "value": "[]",
        "computed": false
      }
    }
  },
  "composes": ["downshift"]
};

export { MultiDownshift as default };
